'use client'

// TODO(phase1-v1.1): non-pointer input alternative
// The a11y contract (§5.0.8) commits to a single production track open to all
// users before the app opens beyond the maintainer. The alternative's UI
// register is deferred to v1.1 amendment. Do NOT ship a split that SRS-tracks
// canvas users but mutes non-canvas users.

import { useReducedMotion } from 'motion/react'
import posthog from 'posthog-js'
import { useCallback, useEffect, useRef, useState } from 'react'

import { CanonicalOverlay } from '@/components/practice/draw-canvas/canonical-overlay'
import {
    type DrawCapturePayload,
    hashTarget,
    roundStrokes,
} from '@/components/practice/draw-canvas/capture-payload'
import {
    DRAW_CANVAS_BOX,
    DRAW_CANVAS_SVG,
    DRAW_VERDICT_CLASS,
} from '@/components/practice/draw-canvas/draw-canvas-box'
import { DrawCanvasSkeleton } from '@/components/practice/draw-canvas/draw-canvas-skeleton'
import { KeywordPrompt } from '@/components/practice/draw-canvas/keyword-prompt'
import { OverridePill } from '@/components/practice/draw-canvas/override-pill'
import { useDrawCapture } from '@/components/practice/draw-canvas/use-draw-capture'
import { DRAW_VERDICTS } from '@/components/practice/draw-canvas/verdicts'
import useKanji from '@/kanjivg/hooks/use-kanji'
import type { KanjiStrokeData } from '@/kanjivg/types'
import {
    gradeDrawing,
    GRADER_VERSION,
    type GraderMode,
    MATCH_THRESHOLD,
    sampleKanjiVgStrokes,
} from '@/lib/grader'
import type { PromptNoun } from '@/lib/prompt-noun'
import { cn } from '@/lib/utils'

export interface DrawCanvasProps {
    /** The kanji being produced. Loads canonical strokes for grading + overlay. */
    character: string
    /** Cache/reset identity for the frame — pass frameNumber whenever the
     *  surface can serve two frames sharing one character (77.1 tool and
     *  78 具 both store 具 but grade different stroke sets). Defaults to
     *  `character` for surfaces that only serve testable kanji (Quiz). */
    dataKey?: string
    /** Keyword prompt shown above the canvas in grading/assessment modes. */
    keyword: string
    /** §5.0.2 v1.0.4 shared-keyword gloss. Resolved by the caller from content;
     *  rendered beneath the keyword and in the accessible name in grading /
     *  assessment modes. Ignored in lenient mode. */
    promptNoun?: PromptNoun
    mode: GraderMode
    /** When true (WI-F brake, §8.7), the override pill is suppressed on reject. */
    overrideBlocked?: boolean
    /** Server-fetched stroke data — skips the client-side POST
     *  fallback in useKanji. Optional; absent call sites keep the fetch. */
    strokeData?: KanjiStrokeData | null
    /** A surface with no next frame (the landing's taster): a resolved
     *  drawing offers "Try again" on a miss and "Draw it again" on a
     *  success, both clearing the ink and returning to the drawing phase,
     *  in place of the concede "Continue →" (§5.0.10, v1.0.3). */
    redraw?: boolean
    /** Fired once per frame with the resolved attempt. */
    onResolve: (result: {
        accepted: boolean
        overridden: boolean
        matchScore: number | null
        /** Wall-clock ms from first pointer-down to resolution; null if
         *  resolved without ever drawing (e.g. kanjiError's Continue). */
        durationMs: number | null
        /** Non-null exactly when the resolution followed a grader rejection
         *  (spec §9.1: rejections only). */
        capture: DrawCapturePayload | null
    }) => void
}

type Phase =
    | 'drawing' // empty or in-progress draw — submit available once ≥1 stroke
    | 'grading' // submit tapped, grader running — submit disabled
    | 'accepted' // grader accepted — verdict shown, step-advance fires
    | 'rejected' // grader rejected — overlay + pill shown
    | 'error' // grader threw or stroke data missing

/**
 * Cross-path draw-canvas primitive (§5.0). ONE component, mode-prop variance.
 *
 * Placement (§5.0.2):
 *   - lenient: canvas appears BELOW the glyph anchor (caller renders glyph)
 *   - grading / assessment: canvas REPLACES the glyph; keyword appears in
 *     text-h1 ink above, with an optional "the kanji" / "the primitive" gloss
 *     beneath it for the shared-keyword pairs (§5.0.2 v1.0.4).
 *
 * Override pill is body-fg ink ONLY — zero vermilion (§5.0.5).
 * Canonical overlay is body-fg ink ONLY — zero vermilion (§5.0.4).
 *
 * Four states (§5.0.10):
 *   empty   — canvas mounted, Submit disabled until ≥1 stroke
 *   loading — KanjiVG data fetching → surface-sunken skeleton
 *   error   — grader throw or missing stroke data → text + retry
 *   success — grader accept → verdict text + step-advance
 *
 * Submission idempotency (§5.0.9):
 *   - empty canvas rejected client-side before grader runs (no pill, no write)
 *   - Submit disabled from tap until onResolve settles
 *
 * a11y (§5.0.8):
 *   - labelled interactive region (aria-label on canvas)
 *   - accept/reject verdicts + override-pill appearance via aria-live="polite"
 *   - verdicts in plain text in feedback line (never overlay-only)
 */
export function DrawCanvas({
    character,
    dataKey,
    keyword,
    promptNoun,
    mode,
    overrideBlocked = false,
    strokeData,
    redraw = false,
    onResolve,
}: DrawCanvasProps) {
    const noun = mode === 'lenient' ? undefined : promptNoun
    const frameKey = dataKey ?? character
    const reduceMotion = useReducedMotion()
    const {
        data: kanjiData,
        loading: kanjiLoading,
        error: kanjiError,
    } = useKanji(character, strokeData, frameKey)
    const {
        strokes,
        current,
        isEmpty,
        svgRef,
        inputTypeRef,
        beginStroke,
        extendStroke,
        endStroke,
        undo,
        clear,
    } = useDrawCapture()

    const [phase, setPhase] = useState<Phase>('drawing')
    const [verdict, setVerdict] = useState<string>('')
    const [errorMessage, setErrorMessage] = useState<string>('')
    const [graderScore, setGraderScore] = useState<number | null>(null)

    // Derived from kanjiData — KanjiVG uses a 109×109 viewport by default.
    const viewBox = kanjiData?.viewBox ?? '0 0 109 109'

    // Track whether we've already fired onResolve (idempotency guard)
    const resolvedRef = useRef(false)
    // Holds the grader accept result pending the dwell timer; null means the
    // accepted phase was reached via override (which already fired onResolve).
    const pendingGraderAcceptRef = useRef<number | null | undefined>(undefined)
    // First pointer-down of the current drawing (spec §2 duration timing).
    // Reset on frame change and on Clear so it always reflects THIS drawing.
    const drawStartRef = useRef<number | null>(null)
    // Set on grader rejection; feeds the capture payload built at resolution
    // (override or concede). Cleared at the start of every fresh submit so a
    // stale rejection can never leak into a later card's capture (spec §9.1).
    const rejectionRef = useRef<{
        perStroke: number[]
        matchScore: number
    } | null>(null)

    // Per-frame reset — fires whenever the frame identity (dataKey ??
    // character) changes. Parents may render a single <DrawCanvas …/> and
    // advance by swapping the frame props WITHOUT remounting. Without
    // this reset, every subsequent frame would render with the previous
    // frame's phase/verdict.
    //
    // Split in two, per the Rules of React:
    //  - Plain useState resets happen DURING RENDER — React's documented
    //    "adjusting some state when a prop changes" pattern
    //    (https://react.dev/learn/you-might-not-need-an-effect). A useEffect
    //    fires AFTER paint, so for one frame the component would render the
    //    OLD verdict/phase against the NEW character before the effect caught
    //    up. Comparing against `lastFrameKey` and calling the setters inline
    //    lets React redo the render before anything commits to the screen: no
    //    flash, and no react-hooks/set-state-in-effect cascading-render
    //    warning.
    //  - Ref resets (`resolvedRef`, `pendingGraderAcceptRef`) and `clear()`
    //    (which itself mutates a ref inside useDrawCapture) stay in a
    //    useEffect below: mutating `.current` during render is unsafe (a
    //    render pass can be discarded/re-run without committing), so refs may
    //    only be written in effects/handlers, never inline with the state
    //    reset above.

    const [lastFrameKey, setLastFrameKey] = useState(frameKey)
    if (frameKey !== lastFrameKey) {
        setLastFrameKey(frameKey)
        setPhase('drawing')
        setVerdict('')
        setErrorMessage('')
        setGraderScore(null)
    }

    useEffect(() => {
        resolvedRef.current = false
        pendingGraderAcceptRef.current = undefined
        drawStartRef.current = null
        // Learn keeps ONE mounted DrawCanvas and swaps frame props (Review/
        // Quiz remount per card instead) — without this reset a mid-session
        // input-device switch (hybrid laptop: mouse → touch) would keep
        // reporting the first-ever device on every later frame's capture.
        // beginStroke's existing 'unknown' guard re-captures it on this
        // frame's first stroke.
        inputTypeRef.current = 'unknown'
        clear()
    }, [frameKey]) // eslint-disable-line react-hooks/exhaustive-deps
    // `clear` identity is stable (useCallback with no deps in useDrawCapture),
    // but listing it would re-trigger the effect when the hook re-renders. The
    // only meaningful dep is `frameKey`; omitting `clear` is intentional.

    // Accept-dwell effect (§5.0.8): when grader accepts, paint the verdict
    // for ACCEPT_DWELL_MS before the parent advances. This prevents React 18
    // auto-batching from collapsing the accepted phase into a single commit with
    // the parent's frame-advance, which would make the "Correct — well drawn."
    // feedback line invisible in Review (and Quiz Stage 2).
    //
    // Only the GRADER accept path triggers this — override sets
    // pendingGraderAcceptRef to undefined (leaving it untouched) and calls
    // onResolve synchronously, so the effect is a no-op for that path.
    //
    // Cleanup clears the timer when character changes or the component unmounts —
    // the per-frame reset effect also runs and clears pendingGraderAcceptRef.

    const ACCEPT_DWELL_MS = 700 // brief enough to feel snappy, long enough to read

    useEffect(() => {
        if (phase !== 'accepted') return
        // pendingGraderAcceptRef is set in handleSubmit only for grader-accepts.
        // If undefined, we arrived via override which already fired onResolve.
        if (pendingGraderAcceptRef.current === undefined) return
        const matchScore = pendingGraderAcceptRef.current
        const timer = setTimeout(() => {
            onResolve({
                accepted: true,
                overridden: false,
                matchScore,
                durationMs: drawStartRef.current
                    ? Date.now() - drawStartRef.current
                    : null,
                capture: null,
            })
        }, ACCEPT_DWELL_MS)
        return () => clearTimeout(timer)
    }, [phase]) // eslint-disable-line react-hooks/exhaustive-deps
    // Intentional: `phase` is the only trigger — matchScore and onResolve are
    // captured from the ref/closure at effect run time (stable within a frame).
    // Adding them as deps would restart the timer on every render, which is wrong.

    // gradeDrawing/sampleKanjiVgStrokes are synchronous — no await needed.
    // Staying non-async also fixes the onClick misused-promises finding
    // below (a plain void-returning function is passed, not a Promise).
    const handleSubmit = useCallback(() => {
        // Idempotency: reject empty canvas before grading (§5.0.9)
        if (isEmpty) return
        // Idempotency: ignore re-taps while grading is pending
        if (phase !== 'drawing') return

        // Fresh grading cycle — a prior rejection must never leak into this
        // attempt's resolution capture (spec §9.1).
        rejectionRef.current = null

        setPhase('grading')

        try {
            if (!kanjiData?.strokePaths || kanjiData.strokePaths.length === 0) {
                throw new Error(
                    'Canonical stroke data is not available for this character.'
                )
            }

            const targetStrokes = sampleKanjiVgStrokes(kanjiData.strokePaths)
            const result = gradeDrawing(strokes, targetStrokes)

            // Dev-only grader telemetry (calibration insight; never in prod).
            if (process.env.NODE_ENV === 'development') {
                console.log('[grader]', {
                    character,
                    keyword,
                    mode,
                    accepted: result.accepted,
                    score: Number(result.score.toFixed(3)),
                    threshold: MATCH_THRESHOLD,
                    perStroke: result.perStroke?.map((s) =>
                        Number(s.toFixed(3))
                    ),
                    userStrokes: strokes.length,
                    targetStrokes: targetStrokes.length,
                })
            }

            setGraderScore(result.score)

            if (result.accepted) {
                // Mark resolved synchronously (idempotency guard) but do NOT call
                // onResolve here — the accept-dwell effect fires it after ~700ms
                // so the "Correct — well drawn." verdict paints before advance.
                // Store matchScore in the ref so the dwell effect can read it.
                resolvedRef.current = true
                pendingGraderAcceptRef.current = result.score
                rejectionRef.current = null
                setVerdict(DRAW_VERDICTS.accepted)
                setPhase('accepted')
            } else {
                rejectionRef.current = {
                    perStroke: result.perStroke ?? [],
                    matchScore: result.score,
                }
                setVerdict(DRAW_VERDICTS.rejected)
                setPhase('rejected')
            }
        } catch (err) {
            const msg =
                err instanceof Error
                    ? err.message
                    : 'An unexpected error occurred during grading.'
            posthog.capture('grader_error', {
                character,
                data_key: frameKey,
                grader_mode: mode,
                message: msg,
            })
            setErrorMessage(
                `Grading failed: ${msg} Your drawing is preserved — you can retry or continue.`
            )
            setPhase('error')
        }
    }, [isEmpty, phase, kanjiData, strokes, character, keyword, mode, frameKey])

    // Builds the rejection-capture payload (spec §9.1) shared by both
    // rejection resolutions below. Returns null when the resolution did NOT
    // follow a grader rejection (e.g. kanjiError's override/Continue, where
    // no grading ever ran) — rejectionRef stays null in that case too.
    const buildCapture = useCallback(
        async (
            resolution: 'overridden' | 'conceded'
        ): Promise<DrawCapturePayload | null> => {
            const rejection = rejectionRef.current
            if (!rejection || !kanjiData?.strokePaths) return null
            return {
                dataKey: frameKey,
                graderMode: mode,
                resolution,
                overrideBlocked,
                matchScore: rejection.matchScore,
                threshold: MATCH_THRESHOLD,
                perStroke: rejection.perStroke,
                userStrokeCount: strokes.length,
                targetStrokeCount: kanjiData.strokePaths.length,
                strokes: roundStrokes(strokes),
                targetHash: await hashTarget(kanjiData.strokePaths, 32),
                graderVersion: GRADER_VERSION,
                drawDurationMs: drawStartRef.current
                    ? Date.now() - drawStartRef.current
                    : null,
                inputType: inputTypeRef.current,
            }
        },
        [frameKey, mode, overrideBlocked, strokes, kanjiData, inputTypeRef]
    )

    const handleOverride = useCallback(async () => {
        if (resolvedRef.current) return
        resolvedRef.current = true
        const capture = await buildCapture('overridden')
        setVerdict(DRAW_VERDICTS.overridden)
        setPhase('accepted')
        onResolve({
            accepted: true,
            overridden: true,
            matchScore: graderScore,
            durationMs: drawStartRef.current
                ? Date.now() - drawStartRef.current
                : null,
            capture,
        })
    }, [graderScore, onResolve, buildCapture])

    const handleAdvanceWithoutOverride = useCallback(async () => {
        if (resolvedRef.current) return
        resolvedRef.current = true
        const capture = await buildCapture('conceded')
        onResolve({
            accepted: false,
            overridden: false,
            matchScore: graderScore,
            durationMs: drawStartRef.current
                ? Date.now() - drawStartRef.current
                : null,
            capture,
        })
    }, [graderScore, onResolve, buildCapture])

    // Void-returning wrappers for JSX event handlers — handleOverride /
    // handleAdvanceWithoutOverride are async (they await buildCapture), and
    // @typescript-eslint/no-misused-promises rejects a Promise-returning
    // function passed where `() => void` is expected (onClick, OverridePill's
    // onOverride prop).
    const onOverrideClick = useCallback(() => {
        void handleOverride()
    }, [handleOverride])

    const onContinueClick = useCallback(() => {
        void handleAdvanceWithoutOverride()
    }, [handleAdvanceWithoutOverride])

    const handleRetry = useCallback(() => {
        // Reset resolvedRef so a subsequent reject/override/continue can fire
        // normally (§5.0.9(c): parent history keying absorbs any double write)
        resolvedRef.current = false
        setPhase('drawing')
        setErrorMessage('')
        // Drawing is preserved (do not clear strokes)
    }, [])

    // Starts the draw-duration timer on the FIRST pointer-down of a drawing
    // (spec §2) — ??= is a no-op on every subsequent stroke within the same
    // drawing, then delegates to useDrawCapture's beginStroke.
    const handlePointerDown = useCallback(
        (e: React.PointerEvent<SVGSVGElement>) => {
            // The ink is read-only once a verdict is in (§5.0.10, v1.0.3):
            // strokes drawn over a rejection fed the capture payload.
            if (phase !== 'drawing') return
            drawStartRef.current ??= Date.now()
            beginStroke(e)
        },
        [beginStroke, phase]
    )

    // Clear also resets the duration timer — the next stroke starts a new
    // drawing, so its timing must not include the discarded one's ink.
    const handleClear = useCallback(() => {
        drawStartRef.current = null
        clear()
    }, [clear])

    // Redraw (§5.0.10, v1.0.3): a resolved drawing starts over on a surface
    // with no next frame. Every per-attempt ref is reset, as on a frame
    // change; a pending accept-dwell is cancelled by the phase change.
    const handleRedraw = useCallback(() => {
        resolvedRef.current = false
        pendingGraderAcceptRef.current = undefined
        rejectionRef.current = null
        drawStartRef.current = null
        clear()
        setVerdict('')
        setGraderScore(null)
        setPhase('drawing')
    }, [clear])

    // iOS Safari recognises pan/zoom gestures on the canvas despite
    // `touch-action: none` (a long-standing quirk: touch-action is unreliable on
    // <svg> in WebKit). Cancelling the default on touchMOVE alone is not enough:
    // Safari latches its scroll recogniser at touchSTART, and once a pan is
    // recognised it runs momentum / rubber-band scrolling that consumes touches
    // for ~seconds before settling — which presents as "I drew two strokes, then
    // the canvas froze, then it came back". Cancelling the default at touchstart
    // stops the gesture from ever being recognised; touchmove stays cancelled to
    // hold the guard for the rest of the drag. Both must be NON-passive and
    // imperative — React's synthetic touch handlers are registered passive and
    // cannot preventDefault. preventDefault on touch does NOT suppress pointer
    // events, so the onPointer* drawing handlers keep firing. Re-runs when the
    // canvas shell mounts (kanjiLoading/kanjiError → false) so it binds to the
    // rendered <svg>.
    useEffect(() => {
        const svg = svgRef.current
        if (!svg) return
        const prevent = (e: TouchEvent) => e.preventDefault()
        svg.addEventListener('touchstart', prevent, { passive: false })
        svg.addEventListener('touchmove', prevent, { passive: false })
        return () => {
            svg.removeEventListener('touchstart', prevent)
            svg.removeEventListener('touchmove', prevent)
        }
    }, [kanjiLoading, kanjiError, svgRef])

    if (kanjiLoading) {
        return (
            <div className="flex w-full flex-col items-center gap-4">
                {mode !== 'lenient' && (
                    <KeywordPrompt keyword={keyword} promptNoun={noun} />
                )}
                <DrawCanvasSkeleton
                    aria-busy="true"
                    aria-label="Loading stroke data…"
                />
            </div>
        )
    }

    // KanjiVG data missing (separate from grader error — no retry, the data
    // genuinely is not there). Per cross-path spec §5.0.10 this must behave as a
    // reject WITH a forward exit, never a dead-end: self-override to mark it
    // correct, or Continue to advance without credit. Reaching this branch is a
    // rare edge (learn-frame's isGradeable gate keeps un-drawable frames out of
    // the draw beat); it remains as a safety net for unexpected load failures.
    if (kanjiError) {
        return (
            <div className="flex w-full flex-col items-center gap-4">
                {mode !== 'lenient' && (
                    <KeywordPrompt keyword={keyword} promptNoun={noun} />
                )}
                <div
                    aria-live="polite"
                    aria-atomic="true"
                    className="flex flex-col items-center gap-2"
                >
                    <p className="font-serif text-sm text-body-fg" role="alert">
                        No stroke data for this character — it cannot be graded
                        automatically.
                    </p>
                    {!overrideBlocked && (
                        <OverridePill onOverride={onOverrideClick} />
                    )}
                </div>
                <button
                    type="button"
                    onClick={onContinueClick}
                    className="font-serif text-sm text-muted-fg transition-colors duration-200 hover:text-body-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring motion-reduce:transition-none"
                >
                    Continue →
                </button>
            </div>
        )
    }

    const isSubmitDisabled = isEmpty || phase !== 'drawing'

    // Capture-layer: strokes drawn by the user rendered as SVG polylines
    const strokeElements = strokes.map((stroke, si) =>
        stroke.length < 2 ? null : (
            <polyline
                key={si}
                points={stroke.map((p) => `${p.x},${p.y}`).join(' ')}
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        )
    )

    // In-progress stroke — rendered live as the user draws, so the trailing
    // mark appears under the pointer instead of only on pointer-up.
    const liveStrokeElement =
        current.length >= 2 ? (
            <polyline
                points={current.map((p) => `${p.x},${p.y}`).join(' ')}
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        ) : null

    // Faint practice-square guides (genkō-yōshi style): a center cross that
    // reads "draw here" and helps balance the kanji. Drawing phase only,
    // body-fg ink, no vermilion. Parsed from the active viewBox so it scales.
    const [gx, gy, gw, gh] = viewBox.split(/\s+/).map(Number)
    const gX0 = gx ?? 0
    const gY0 = gy ?? 0
    const gW = gw ?? 109
    const gH = gh ?? 109

    return (
        <div className="flex w-full flex-col items-center gap-4">
            {/* Keyword prompt — only in grading/assessment modes (§5.0.2) */}
            {mode !== 'lenient' && (
                <KeywordPrompt keyword={keyword} promptNoun={noun} />
            )}

            {/* Em-box canvas (§5.0.2) */}
            <div className={cn('relative', DRAW_CANVAS_BOX)}>
                {/* Drawing SVG layer */}
                <svg
                    ref={svgRef}
                    viewBox={viewBox}
                    aria-label={`Draw the ${noun ?? 'kanji'} for ${keyword}`}
                    className={[
                        DRAW_CANVAS_SVG,
                        // pointer input only while drawing (§5.0.10, v1.0.3)
                        phase !== 'drawing' ? 'pointer-events-none' : '',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                    onPointerDown={handlePointerDown}
                    onPointerMove={extendStroke}
                    onPointerUp={endStroke}
                    onPointerLeave={endStroke}
                    onPointerCancel={endStroke}
                >
                    {phase === 'drawing' ? (
                        <g
                            className="text-body-fg"
                            stroke="currentColor"
                            strokeWidth={0.6}
                            strokeDasharray="2.5 3.5"
                            opacity={0.12}
                            aria-hidden="true"
                        >
                            <line
                                x1={gX0 + gW / 2}
                                y1={gY0}
                                x2={gX0 + gW / 2}
                                y2={gY0 + gH}
                            />
                            <line
                                x1={gX0}
                                y1={gY0 + gH / 2}
                                x2={gX0 + gW}
                                y2={gY0 + gH / 2}
                            />
                        </g>
                    ) : null}
                    {strokeElements}
                    {liveStrokeElement}
                </svg>

                {/* Canonical overlay — only on reject/error (§5.0.4).
                    Reveal-and-resolve: fades in unless reduced motion (§5.0.7 → instant). */}
                {(phase === 'rejected' || phase === 'error') && kanjiData && (
                    <div
                        className={[
                            'absolute inset-0',
                            reduceMotion
                                ? ''
                                : 'animate-in duration-300 fade-in',
                        ]
                            .filter(Boolean)
                            .join(' ')}
                    >
                        <CanonicalOverlay
                            kanjiData={kanjiData}
                            viewBox={viewBox}
                        />
                    </div>
                )}
            </div>

            {/* aria-live region for verdicts + override pill (§5.0.8) */}
            <div
                aria-live="polite"
                aria-atomic="true"
                className="flex flex-col items-center gap-2"
            >
                {/* Feedback / verdict line — plain text (never overlay-only) */}
                {phase === 'accepted' && (
                    <p className={DRAW_VERDICT_CLASS}>{verdict}</p>
                )}

                {phase === 'rejected' && (
                    <>
                        <p className={DRAW_VERDICT_CLASS}>{verdict}</p>
                        {/* Override pill — only when overrideBlocked is false (§8.7 WI-F brake) */}
                        {!overrideBlocked && (
                            <OverridePill onOverride={onOverrideClick} />
                        )}
                    </>
                )}

                {phase === 'error' && (
                    <>
                        <p
                            className="font-serif text-sm text-body-fg"
                            role="alert"
                        >
                            {errorMessage}
                        </p>
                        {/* Override pill in the live region so its appearance is
                            announced to screen readers — mirrors rejected-phase
                            structure (§5.0.8) */}
                        {!overrideBlocked && (
                            <OverridePill onOverride={onOverrideClick} />
                        )}
                    </>
                )}
            </div>

            {/* Submit / action row */}
            <div className="flex flex-wrap items-center justify-center gap-3">
                {(phase === 'drawing' || phase === 'grading') && (
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSubmitDisabled}
                        className="inline-flex h-11 items-center justify-center rounded-sm bg-action px-6 font-serif text-base font-medium tracking-wide text-action-on transition-colors duration-200 hover:bg-action-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring active:translate-y-px active:bg-action-pressed disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none motion-reduce:active:translate-y-0"
                    >
                        {phase === 'grading' ? 'Checking…' : 'Submit'}
                    </button>
                )}

                {/* Undo last stroke — incremental, sits between Submit and the
                    destructive Clear. Only while drawing with ≥1 stroke. */}
                {phase === 'drawing' && !isEmpty && (
                    <button
                        type="button"
                        onClick={undo}
                        className="font-serif text-sm text-muted-fg transition-colors duration-200 hover:text-body-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring motion-reduce:transition-none"
                    >
                        Undo
                    </button>
                )}

                {/* Clear strokes — only while drawing */}
                {phase === 'drawing' && !isEmpty && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className="font-serif text-sm text-muted-fg transition-colors duration-200 hover:text-body-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring motion-reduce:transition-none"
                    >
                        Clear
                    </button>
                )}

                {/* Reject: advance without override (fires onResolve
                    accepted:false) — or, with no next frame, draw again. */}
                {phase === 'rejected' && (
                    <button
                        type="button"
                        onClick={redraw ? handleRedraw : onContinueClick}
                        className="font-serif text-sm text-muted-fg transition-colors duration-200 hover:text-body-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring motion-reduce:transition-none"
                    >
                        {redraw ? 'Try again' : 'Continue →'}
                    </button>
                )}

                {/* Accept with no next frame: the ink stays until the
                    visitor starts over. */}
                {phase === 'accepted' && redraw && (
                    <button
                        type="button"
                        onClick={handleRedraw}
                        className="font-serif text-sm text-muted-fg transition-colors duration-200 hover:text-body-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring motion-reduce:transition-none"
                    >
                        Draw it again
                    </button>
                )}

                {/* Error: retry (drawing preserved — §5.0.10) */}
                {phase === 'error' && (
                    <>
                        <button
                            type="button"
                            onClick={handleRetry}
                            className="inline-flex h-11 items-center justify-center rounded-sm bg-action px-6 font-serif text-base font-medium tracking-wide text-action-on transition-colors duration-200 hover:bg-action-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none"
                        >
                            Retry
                        </button>
                        {!redraw && (
                            <button
                                type="button"
                                onClick={onContinueClick}
                                className="font-serif text-sm text-muted-fg transition-colors duration-200 hover:text-body-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring motion-reduce:transition-none"
                            >
                                Continue →
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
